import clsx from "clsx";
import { Link } from "react-router-dom";

export interface DashboardAlert {
  id: string;
  tone: "info" | "warning" | "danger";
  title: string;
  detail?: string;
  href?: string;
  hrefLabel?: string;
}

const TONE_STYLES: Record<DashboardAlert["tone"], string> = {
  info: "border-accent/30 bg-accent-soft text-label",
  warning: "border-status-orange/35 bg-status-orange/10 text-label",
  danger: "border-status-red/35 bg-status-red/10 text-label",
};

interface DashboardAlertsProps {
  alerts: DashboardAlert[];
  className?: string;
  /** Kartenstapel oder eine Zeile, die die Arbeitsfläche nicht verdrängt. */
  variant?: "cards" | "inline";
}

export function DashboardAlerts({ alerts, className, variant = "cards" }: DashboardAlertsProps) {
  if (alerts.length === 0) return null;

  if (variant === "inline") {
    return (
      <div
        className={clsx("dashboard-alerts flex gap-2 overflow-x-auto overscroll-contain", className)}
        role="region"
        aria-label="Hinweise und Warnungen"
      >
        {alerts.map((alert) => {
          const chipClass = clsx(
            "inline-flex max-w-[20rem] shrink-0 items-center rounded-full border px-3 py-1.5 text-xs font-semibold",
            TONE_STYLES[alert.tone],
          );
          const label = <span className="truncate">{alert.title}</span>;
          if (!alert.href) {
            return (
              <p key={alert.id} className={chipClass} title={alert.detail}>
                {label}
              </p>
            );
          }
          return (
            <Link key={alert.id} to={alert.href} className={chipClass} title={alert.detail ?? alert.hrefLabel}>
              {label}
            </Link>
          );
        })}
      </div>
    );
  }

  return (
    <div
      className={clsx(
        "dashboard-alerts flex flex-col gap-2 lg:grid lg:grid-cols-2 lg:gap-3 xl:grid-cols-3",
        className,
      )}
      role="region"
      aria-label="Hinweise und Warnungen"
    >
      {alerts.length > 1 ? (
        <p className="px-0.5 text-xs font-semibold uppercase tracking-wide text-label-tertiary lg:hidden">
          {alerts.length} Hinweise
        </p>
      ) : null}
      {alerts.map((alert) => (
        <div
          key={alert.id}
          className={clsx(
            "flex flex-wrap items-center justify-between gap-2 rounded-xl border px-3 py-2 sm:px-4 sm:py-2.5",
            TONE_STYLES[alert.tone],
          )}
        >
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold leading-snug">{alert.title}</p>
            {alert.detail ? (
              <p className="mt-0.5 line-clamp-2 text-xs text-label-secondary sm:line-clamp-none">
                {alert.detail}
              </p>
            ) : null}
          </div>
          {alert.href ? (
            <Link
              to={alert.href}
              className="shrink-0 rounded-lg bg-card/60 px-2.5 py-1 text-xs font-semibold text-accent sm:bg-transparent sm:px-0 sm:py-0 sm:text-sm sm:font-medium hover:underline"
            >
              {alert.hrefLabel ?? "Öffnen"}
            </Link>
          ) : null}
        </div>
      ))}
    </div>
  );
}
